import React, { useState, useEffect } from "react";
import { TextField, MenuItem, Grid, Typography, CircularProgress, Button, Collapse, Chip, ListItemText, Checkbox, Box } from "@mui/material";
import { FilterList as FilterListIcon } from "@mui/icons-material";
import axios from "axios";
import PrePostLaminationTable from "./FormTables/PrePostLaminationTable";
import BOMTable from "./FormTables/BOMTable";
import DailyVolumeTable from "./FormTables/DailyVolumeTable";
import StringerTable from "./FormTables/StringerTable";
import CellToRibbonPeelStrengthTable from "./FormTables/CellToRibbonPeelStrengthTable";
import PeelStrengthTable from "./FormTables/PeelStrengthTable";
import LaminationMachineTable from "./FormTables/LaminationMachineTable";
import CalibrationVerificationTable from "./FormTables/CalibrationVerificationTable";
import FlashTestWitnessTable from "./FormTables/FlashTestWitnessTable";
import PrimaryModuleCalibrationTable from "./FormTables/PrimaryModuleCalibrationTable";
import FrameSealantWeightTable from "./FormTables/FrameSealantWeightTable";
import JunctionBoxSealantWeightTable from "./FormTables/JunctionBoxSealantWeightTable";
import JunctionBoxPottingMaterialTable from "./FormTables/JunctionBoxPottingMaterialTable";
import ModuleDimensionCheckTable from "./FormTables/ModuleDimensionCheckTable";
import RecordRejectedModuleTable from "./FormTables/RecordRejectedModuleTable";
import RFIDReadabilityTable from "./FormTables/RFIDReadabilityTable";
import MachineBreakdownHistoryTable from "./FormTables/MachineBreakdownHistoryTable";
import RootCauseAnalysisTable from "./FormTables/RootCauseAnalysisTable";

const formMappings = {
  "Pre-Lamination": "PreLamination",
  "Post-Lamination": "PostLamination",
  "Bill of material verification": "BOM",
  "Daily volume of the qualified modules": "dailyVolumeQualifiedModules",
  "Stringer": "stringer",
  "Peel Strength Of Ribbon To String Connector": "PeelStrength",
  "Cell To Ribbon Peel Strength": "CellToRibbonPeelStrength",
  "Lamination machine parameters verification": "LaminationMachine",
  "Calibration data verification of secondary module": "calibrationVerificationOfSecondaryModule",
  "Flash test witness record after calibration": "FlashTestWitnessRecordAfterCalibration",
  "Primary (Golden) Module Calibration record": "PrimaryModuleCalibrationRecord",
  "Frame sealant weight verification": "FrameSealantWeightVerification",
  "Junction Box sealant weight verification": "JunctionBoxSealantWeightVerification",
  "Junction Box Potting material ratio verification": "JunctionBoxPottingMaterial",
  "Module dimensions check": "ModuleDimensionCheck",
  "Record of rejected module during inspection": "RecordRejectedModuleDuringInspection",
  "RFID Readability and Data Verification": "RFIDReadability",
  "Machine Breakdown History": "MachineBreakdownHistory",
  "Root Cause Analysis": "RootCauseAnalysis",
};

// Sub-form mappings for Pre-Lamination
const preLaminationSubFormMappings = {
  "Shop Floor (Pre)": "shopFloor_pre",
  "Cell and Cutting Machine (Pre)": "CellAndCuttingMachine_pre",
  "Encapsulation Material (Front) (Pre)": "encapsulationMaterialFront_pre",
  "Encapsulation Material (Back) (Pre)": "encapsulationMaterialBack_pre",
  "Glass (Front & Back) (Pre)": "GlassFrontBack_pre",
  "Tabbing & Stringing (Pre)": "TabbingStringing_pre",
  "Auto Bussing (Pre)": "autoBussing_pre",
  "Barcode/RFID (Pre)": "barCodeRFID_pre",
  "Pre-Lamination EL & Re-work (Pre)": "PreLaminationELRework_pre",
};

// Sub-form mappings for Post-Lamination
const postLaminationSubFormMappings = {
  "Lamination (Post)": "Lamination_post",
  "Trimming/VI at 90 Degree (Post)": "Trimming_post",
  "Framing (Post)": "Framing_post",
  "JB Assembling & Soldering (Post)": "JbAssemblingSoldering_post",
  "Curing (Post)": "Curing_post",
  "Cleaning (Post)": "Cleaning_post",
  "Electrical Test (Post)": "electricalTest_post",
  "HiPot (Post)": "HiPot_post",
  "Post-Lamination EL (Post)": "PostLaminationEL_post",
  "Visual Inspection (Post)": "VisualInspections_post",
  "Packing (Post)": "Packing_post",
};

// Sub-form mappings for BOM
const bomSubFormMappings = {

  "Adhesive Sealant (BOM)": "AdhesiveSealant",
  "Cell (BOM)": "Cell",
  "Frame (BOM)": "Frame",
  "Cell Fixing Type (BOM)": "cellFixingType",
  "EVA Back (BOM)": "evaBack",
  "EVA Front (BOM)": "evaFront",
  "Flux (BOM)": "flux",
  "Glass Back (BOM)": "glassBack",
  "Glass Front (BOM)": "glassFront",
  "Interconnector (BOM)": "interconnector",
  "Junction Box (BOM)": "junctionBox",
  "Potting Material (BOM)": "pottingMaterial",
  "String Connector (BOM)": "stringConnector",
};


const InspectionSummary = () => {
  const [supplier, setSupplier] = useState("");
  const [shift, setShift] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [formName, setFormName] = useState([]);
  const [subForm, setSubForm] = useState([]);
  const [tableData, setTableData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierShifts, setSupplierShifts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Form names for UI display
  const formNames = Object.keys(formMappings);

  // Fetch suppliers when the component mounts
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const selectedProject = localStorage.getItem("selectedProject");
        if (!selectedProject) {
          setError("Project ID not found in local storage");
          return;
        }

        const response = await axios.post(`${baseURL}get_supplier_names`, {
          projectId: selectedProject,
        });

        if (!Array.isArray(response.data)) {
          setError("Invalid response format from supplier API");
          return;
        }

        const supplierData = response.data
          .map((info) => {
            if (!info.name) {
              console.warn("Skipping invalid supplier entry:", info);
              return null;
            }
            return {
              name: info.name,
              shift: info.shift,
            };
          })
          .filter(Boolean);

        if (supplierData.length === 0) {
          setError("No valid suppliers found");
        }

        setSuppliers(supplierData);
      } catch (error) {
        setError("Error collecting supplier information");
        console.error("Supplier fetch error:", error);
      }
    };
    fetchSuppliers();
  }, []);

  // Handle supplier change
  const handleSupplierChange = (e) => {
    const selectedSupplierName = e.target.value;
    setSupplier(selectedSupplierName);
    setShift([]);

    const selectedSupplierData = suppliers.find((sup) => sup.name === selectedSupplierName);
    if (selectedSupplierData) {
      const shiftOptions =
        selectedSupplierData.shift === "MorningAfternoonNight"
          ? ["Morning", "Afternoon", "Night"]
          : ["Day", "Night"];
      setSupplierShifts(shiftOptions);
    } else {
      setSupplierShifts([]);
    }
  };

  // Fetch inspection summary data
  useEffect(() => {
    const fetchData = async () => {
      const projectId = localStorage.getItem("selectedProject");
      if (
        !projectId ||
        !supplier ||
        shift.length === 0 ||
        !startDate ||
        !endDate ||
        formName.length === 0 ||
        (formName.some((form) =>
          ["Pre-Lamination", "Post-Lamination", "Bill of material verification"].includes(form)
        ) && subForm.length === 0)
      ) {
        setTableData({});
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const apiFormNames = formName.map((name) => formMappings[name]);
        const apiSubForms = subForm.map((name) =>
          preLaminationSubFormMappings[name] ||
          postLaminationSubFormMappings[name] ||
          bomSubFormMappings[name]
        ).filter(Boolean);
        
        const response = await axios.get(
          `${baseURL}inspection_summary`,
          {
            params: {
              projectId,
              inspectionSupplier: supplier,
              inspectionShift: shift.join(","),
              date: startDate,
              endDate,
              formName: apiFormNames.join(","),
              subForm: apiSubForms.join(","),
            },
          }
        );

        if (response.data.status === "success") {
          const groupedData = formName.reduce((acc, uiFormName) => {
            const apiFormName = formMappings[uiFormName];
            acc[uiFormName] = [];

            const dataArray = Array.isArray(response.data.data)
              ? response.data.data
              : [];
            dataArray.forEach((item, index) => {

              // Handle Pre/Post Lamination forms
              if (item[apiFormName]) {
                const subFormData = item[apiFormName];
                Object.entries(subFormData).forEach(([apiSubForm, subFormDetails]) => {
                  if (apiSubForms.length === 0 || apiSubForms.includes(apiSubForm)) {
                    // Handle Tabbing & Stringing specifically
                    if (apiSubForm === "TabbingStringing_pre" && subFormDetails.TabbingStringing) {
                      Object.entries(subFormDetails.TabbingStringing).forEach(([tableName, observations]) => {
                        if (Array.isArray(observations)) {
                          observations.forEach((obs) => {
                            acc[uiFormName].push({
                              inspectionDate: item.inspectionDate ?? "N/A",
                              inspectionShift: item.inspectionShift ?? "N/A",
                              inspectionSupplier: item.inspectionSupplier ?? "N/A",
                              inspectorName: item.inspectorName ?? "N/A",
                              projectId,
                              status: item.status ?? "N/A",
                              subForm: apiSubForm,
                              tableName,
                              field: obs.product ?? obs.field ?? "N/A",
                              frequency: obs.frequency ?? "N/A",
                              specification: obs.specifications ?? obs.specification ?? "N/A",
                              obs1: obs.observation1 ?? obs.obs1 ?? "N/A",
                              obs2: obs.observation2 ?? obs.obs2 ?? "N/A",
                              remarks: subFormDetails.nonConformance ?? "N/A",
                            });
                          });
                        }
                      });
                    } else if (apiSubForm === "VisualInspections_post" && subFormDetails) {
                      // Handle VisualInspections_post
                      Object.entries(subFormDetails).forEach(([tableName, observations]) => {
                        if (tableName === "nonConformance") return;
                        if (Array.isArray(observations) && tableName !== "ModuleInspection") {
                          observations.forEach((obs) => {
                            acc[uiFormName].push({
                              inspectionDate: item.inspectionDate ?? "N/A",
                              inspectionShift: item.inspectionShift ?? "N/A",
                              inspectionSupplier: item.inspectionSupplier ?? "N/A",
                              inspectorName: item.inspectorName ?? "N/A",
                              projectId,
                              status: item.status ?? "N/A",
                              subForm: apiSubForm,
                              tableName,
                              field: obs.product ?? obs.field ?? "N/A",
                              frequency: obs.frequency ?? "N/A",
                              specification: obs.specifications ?? obs.specification ?? "N/A",
                              obs1: obs.observation1 ?? obs.obs1 ?? "N/A",
                              obs2: obs.observation2 ?? obs.obs2 ?? "N/A",
                              remarks: subFormDetails.nonConformance ?? "N/A",
                            });
                          });
                        }
                        // Case: ModuleInspection table
                        if (tableName === "ModuleInspection" && Array.isArray(observations)) {
                          observations.forEach((module) => {
                            acc[uiFormName].push({
                              inspectionDate: item.inspectionDate ?? "N/A",
                              inspectionShift: item.inspectionShift ?? "N/A",
                              inspectionSupplier: item.inspectionSupplier ?? "N/A",
                              inspectorName: item.inspectorName ?? "N/A",
                              projectId,
                              status: item.status ?? "N/A",
                              subForm: apiSubForm,
                              tableName,
                              moduleInspection: module.moduleNumber ?? "N/A",
                              moduleStatus: module.status ?? "N/A",
                              remarks: module.remarks ?? "", // Use module.remarks directly
                            });
                          });
                        }
                      });
                    } else {
                      // Handle other subforms
                      Object.entries(subFormDetails).forEach(([tableName, value]) => {
                        if (tableName === "nonConformance" || tableName === "ModuleInspection") return; // Skip ModuleInspection
                        if (Array.isArray(value)) {
                          value.forEach((obs) => {
                            acc[uiFormName].push({
                              inspectionDate: item.inspectionDate ?? "N/A",
                              inspectionShift: item.inspectionShift ?? "N/A",
                              inspectionSupplier: item.inspectionSupplier ?? "N/A",
                              inspectorName: item.inspectorName ?? "N/A",
                              projectId,
                              status: item.status ?? "N/A",
                              subForm: apiSubForm,
                              tableName,
                              field: obs.field ?? obs.product ?? "N/A",
                              frequency: obs.frequency ?? "N/A",
                              specification: obs.specifications ?? obs.specification ?? "N/A",
                              obs1: obs.observation1 ?? obs.obs1 ?? "N/A",
                              obs2: obs.observation2 ?? obs.obs2 ?? "N/A",
                              remarks: subFormDetails.nonConformance ?? "N/A",
                              moduleInspection: obs.moduleNumber ?? "N/A",
                            });
                          });
                        }
                      });
                    }
                  }
                });
              }              
              // Handle Bill of material verification
              else if (
                uiFormName === "Bill of material verification" &&
                item.form_name === "BOM"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) 
                {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    zip_url: item.zip_url || "N/A",
                    form_data: item.form_data || {},
                  });
                }
                
              }
              // Handle Daily volume of the qualified modules
              else if (
                uiFormName === "Daily volume of the qualified modules" &&
                item.form_name === "dailyVolumeQualifiedModules"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Stringer
              else if (
                uiFormName === "Stringer" &&
                item.form_name === "stringer"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Cell To Ribbon Peel Strength
              else if (
                uiFormName === "Cell To Ribbon Peel Strength" &&
                item.form_name === "CellToRibbonPeelStrength"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                    zip_url: item.zip_url || "N/A",
                  });
                }
              }
              // Handle Peel Strength Of Ribbon To String Connector
              else if (
                uiFormName === "Peel Strength Of Ribbon To String Connector" &&
                item.form_name === "PeelStrength"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                    zip_url: item.zip_url || "N/A",
                  });
                }
              }
              // Handle Lamination machine parameters verification
              else if (
                uiFormName === "Lamination machine parameters verification" &&
                item.form_name === "LaminationMachine"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Calibration data verification of secondary module
              else if (
                uiFormName === "Calibration data verification of secondary module" &&
                item.form_name === "calibrationVerificationOfSecondaryModule"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Flash test witness record after calibration
              else if (
                uiFormName === "Flash test witness record after calibration" &&
                item.form_name === "FlashTestWitnessRecordAfterCalibration"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Primary (Golden) Module Calibration record
              else if (
                uiFormName === "Primary (Golden) Module Calibration record" &&
                item.form_name === "PrimaryModuleCalibrationRecord"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Frame sealant weight verification
              else if (
                uiFormName === "Frame sealant weight verification" &&
                item.form_name === "FrameSealantWeightVerification"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Junction Box sealant weight verification
              else if (
                uiFormName === "Junction Box sealant weight verification" &&
                item.form_name === "JunctionBoxSealantWeightVerification"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Junction Box Potting material ratio verification
              else if (
                uiFormName === "Junction Box Potting material ratio verification" &&
                item.form_name === "JunctionBoxPottingMaterial"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Module dimensions check
              else if (
                uiFormName === "Module dimensions check" &&
                item.form_name === "ModuleDimensionCheck"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Record of rejected module during inspection
              else if (
                uiFormName === "Record of rejected module during inspection" &&
                item.form_name === "RecordRejectedModuleDuringInspection"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle RFID Readability and Data Verification
              else if (
                uiFormName === "RFID Readability and Data Verification" &&
                item.form_name === "RFIDReadability"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                    zip_url: item.zip_url || "N/A",
                  });
                }
              }
              // Handle Machine Breakdown History
              else if (
                uiFormName === "Machine Breakdown History" &&
                item.form_name === "MachineBreakdownHistory"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                  });
                }
              }
              // Handle Root Cause Analysis
              else if (
                uiFormName === "Root Cause Analysis" &&
                item.form_name === "RootCauseAnalysis"
              ) {
                if (
                  apiSubForms.length === 0 ||
                  !item.sub_form ||
                  apiSubForms.includes(item.sub_form)
                ) {
                  acc[uiFormName].push({
                    inspectionDate: item.inspectionDate ?? "N/A",
                    inspectionShift: item.inspectionShift ?? "N/A",
                    inspectionSupplier: item.inspectionSupplier ?? "N/A",
                    inspectorName: item.inspectorName ?? "N/A",
                    projectId: item.projectId ?? "N/A",
                    status: item.status ?? "N/A",
                    form_data: item.form_data || {},
                    zip_url: item.zip_url || "N/A",
                  });
                }
              }
            });
            return acc;
          }, {});
          setTableData(groupedData);
        } else {
          setError(response.data.message || "Failed to fetch data");
          setTableData({});
        }
      } catch (err) {
        setError("Failed to fetch data");
        console.error("Fetch error:", err);
        setTableData({});
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [supplier, shift, startDate, endDate, formName, subForm]);

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  // Determine available subforms
  const availableSubForms = (() => {
    let subforms = [];
    if (formName.includes("Pre-Lamination")) {
      subforms = [...subforms, ...Object.keys(preLaminationSubFormMappings)];
    }
    if (formName.includes("Post-Lamination")) {
      subforms = [...subforms, ...Object.keys(postLaminationSubFormMappings)];
    }
    if (formName.includes("Bill of material verification")) {
      subforms = [...subforms, ...Object.keys(bomSubFormMappings)];
    }
    return [...new Set(subforms)];
  })();

  const handleFormNameChange = (e) => {
    let selectedForms = e.target.value;   

    const lastClicked = selectedForms[selectedForms.length - 1];

    if (lastClicked === "all") {
      
      if (formName.length === formNames.length) {
        selectedForms = [];               
      } else {
        selectedForms = [...formNames];   
      }
    }

    selectedForms = selectedForms.filter(item => item !== "all");

    setFormName(selectedForms);

    const requiredForms = [
      "Pre-Lamination",
      "Post-Lamination",
      "Bill of material verification"
    ];

    const hasAnyRequired = requiredForms.some(form => selectedForms.includes(form));

    if (!hasAnyRequired) {
      setSubForm([]);                              
    } else {
      
      const newSubForms = subForm.filter(sub => availableSubForms.includes(sub));
      setSubForm(newSubForms);
    }
  };

  const handleSubFormChange = (e) => {
    let value = e.target.value;
    if (value[value.length - 1] === "all") {
      setSubForm(subForm.length === availableSubForms.length ? [] : [...availableSubForms]);
      return;
    }
    setSubForm(value.filter(v => v !== "all"));
  };

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h5" gutterBottom>
        Inspection Summary
      </Typography>

      <Button
        variant="outlined"
        startIcon={<FilterListIcon />}
        onClick={toggleFilters}
        style={{ marginBottom: "20px" }}
      >
        {showFilters ? "Hide Filters" : "Show Filters"}
      </Button>

      <Collapse in={showFilters}>
        <Grid container spacing={2} style={{ marginBottom: "20px" }}>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Supplier"
              value={supplier}
              onChange={handleSupplierChange}
              fullWidth
              size="small"
            >
              {suppliers.map((option) => (
                <MenuItem key={option.name} value={option.name}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Shift"
              value={shift}
              onChange={(e) => setShift(e.target.value)}
              fullWidth
              size="small"
              disabled={!supplier}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </div>
                ),
              }}
            >
              {supplierShifts.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <TextField
              select
              label="Form Name"
              value={formName}
              onChange={handleFormNameChange}
              fullWidth
              size="small"
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <div sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </div>
                ),
              }}
            >
              <MenuItem
                value="all"
                sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}
              >
                <Checkbox
                  checked={formName.length === formNames.length && formNames.length > 0}
                  indeterminate={formName.length > 0 && formName.length < formNames.length}
                />
                <ListItemText
                  primary={
                    formName.length === formNames.length && formNames.length > 0
                      ? 'Deselect All'
                      : 'Select All'
                  }
                />
              </MenuItem>
              {formNames.map((option) => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={formName.indexOf(option) > -1} />
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </TextField>
          </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                select
                label="Sub Form"
                value={subForm}
                onChange={handleSubFormChange}  // ← Updated handler
                fullWidth
                size="small"
                disabled={availableSubForms.length === 0}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (
                    <div sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </div>
                  ),
                }}
              >
                <MenuItem value="all" sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}>
                  <Checkbox
                    checked={subForm.length === availableSubForms.length && availableSubForms.length > 0}
                    indeterminate={subForm.length > 0 && subForm.length < availableSubForms.length}
                  />
                  <ListItemText primary={subForm.length === availableSubForms.length ? 'Deselect All' : 'Select All'} />
                </MenuItem>

                {availableSubForms.map((option) => (
                  <MenuItem key={option} value={option}>
                    <Checkbox checked={subForm.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
        </Grid>
      </Collapse>

      {formName.some((form) =>
        ["Pre-Lamination", "Post-Lamination", "Bill of material verification"].includes(form)
      ) && subForm.length === 0 && (
        <Typography variant="h6" color="textSecondary" align="center" sx={{ mt: 2 }}>
          Please select a subform to view the data.
        </Typography>
      )}

      {loading && (
        <div style={{ textAlign: "center", margin: "20px 0" }}>
          <CircularProgress />
        </div>
      )}
      {error && (
        <Typography color="error" align="center" style={{ margin: "20px 0" }}>
          {error}
        </Typography>
      )}
      {formName.map((form) => {
        if (form === "Pre-Lamination" || form === "Post-Lamination") {
          return (
            <PrePostLaminationTable
              key={form}
              formName={form}
              data={tableData[form] || []}
              subForm={subForm}
              subFormMappings={
                form === "Pre-Lamination"
                  ? preLaminationSubFormMappings
                  : postLaminationSubFormMappings
              }
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
            />
          );
        } else if (form === "Bill of material verification") {
          return (
            <BOMTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Daily volume of the qualified modules") {
          return (
            <DailyVolumeTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Stringer") {
          return (
            <StringerTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Cell To Ribbon Peel Strength") {
          return (
            <CellToRibbonPeelStrengthTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Peel Strength Of Ribbon To String Connector") {
          return (
            <PeelStrengthTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Lamination machine parameters verification") {
          return (
            <LaminationMachineTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Calibration data verification of secondary module") {
          return (
            <CalibrationVerificationTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Flash test witness record after calibration") {
          return (
            <FlashTestWitnessTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Primary (Golden) Module Calibration record") {
          return (
            <PrimaryModuleCalibrationTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Frame sealant weight verification") {
          return (
            <FrameSealantWeightTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Junction Box sealant weight verification") {
          return (
            <JunctionBoxSealantWeightTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Junction Box Potting material ratio verification") {
          return (
            <JunctionBoxPottingMaterialTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Module dimensions check") {
          return (
            <ModuleDimensionCheckTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Record of rejected module during inspection") {
          return (
            <RecordRejectedModuleTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "RFID Readability and Data Verification") {
          return (
            <RFIDReadabilityTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Machine Breakdown History") {
          return (
            <MachineBreakdownHistoryTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        } else if (form === "Root Cause Analysis") {
          return (
            <RootCauseAnalysisTable
              key={form}
              data={tableData[form] || []}
              startDate={startDate}
              endDate={endDate}
              shifts={shift}
              subForm={subForm}
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default InspectionSummary;